import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";

interface ContactLabels {
  title: string;
  dataSecure: string;
  intro: string;
  authNote: string;
  nameLabel: string;
  namePlaceholder: string;
  nameRequired: string;
  nameNoNumbers: string;
  nameMinLength: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequired: string;
  phoneInvalidChars: string;
  phoneMinLength: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailRequired: string;
  emailInvalid: string;
  consentLabel: string;
  privacyPolicy: string;
  consentLabelEnd: string;
}

interface ContactSectionProps {
  name: string;
  phone: string;
  email: string;
  consent: boolean;
  onNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onConsentChange: (checked: boolean) => void;
  onOpenPrivacyDialog: () => void;
  labels: ContactLabels;
}

export const ContactSection = ({
  name,
  phone,
  email,
  consent,
  onNameChange,
  onPhoneChange,
  onEmailChange,
  onConsentChange,
  onOpenPrivacyDialog,
  labels,
}: ContactSectionProps) => {
  const { isAuthenticated, user } = useAuth();
  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
  });

  const validateName = (value: string) => {
    if (!value.trim()) return labels.nameRequired;
    if (/\d/.test(value)) return labels.nameNoNumbers;
    if (value.length < 2) return labels.nameMinLength;
    return "";
  };

  const validatePhone = (value: string) => {
    if (!value.trim()) return labels.phoneRequired;
    if (!/^[\d\s\-+()]+$/.test(value)) return labels.phoneInvalidChars;
    if (value.replace(/\D/g, "").length < 7) return labels.phoneMinLength;
    return "";
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) return labels.emailRequired;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return labels.emailInvalid;
    return "";
  };

  const handleNameChange = (value: string) => {
    onNameChange(value);
    setErrors((prev) => ({ ...prev, name: validateName(value) }));
  };

  const handlePhoneChange = (value: string) => {
    onPhoneChange(value);
    setErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
  };

  const handleEmailChange = (value: string) => {
    onEmailChange(value);
    setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Nästan slutfört</h2>
      <p className="text-sm">Din personliga data är säker med oss!</p>
      <p className="text-sm italic">
        Vi kommer ge dig en gratis och icke bindande inbytes värdering på din
        bil. För att göra detta behöver vi lite mer information.
      </p>

      {isAuthenticated && (
        <p className="text-blue-500 text-xs">
          Din personliga data hämtas från ditt konto och du kan därför inte
          redigera något. Vill du ändra på något värde, måste du gå ändra i
          profilinställningar
        </p>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium">
            {labels.nameLabel} <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            placeholder={labels.namePlaceholder}
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            className={errors.name ? "border-red-500" : ""}
            readOnly={isAuthenticated}
          />
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">
            {labels.phoneLabel} <span className="text-red-500">*</span>
          </label>
          <Input
            type="tel"
            placeholder={labels.phonePlaceholder}
            required
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            className={errors.phone ? "border-red-500" : ""}
            readOnly={isAuthenticated}
          />
          {errors.phone && (
            <p className="text-red-500 text-xs">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">
            {labels.emailLabel} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              type="email"
              placeholder={labels.emailPlaceholder}
              required
              value={email.toLowerCase()}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
              readOnly={isAuthenticated}
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs">{errors.email}</p>
          )}
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="consent"
            checked={consent}
            onCheckedChange={(checked) => onConsentChange(checked as boolean)}
            className="mt-1"
          />
          <div className="flex-1">
            <label htmlFor="consent" className="text-sm">
              {labels.consentLabel}
              <button
                type="button"
                onClick={onOpenPrivacyDialog}
                className="text-blue-600 hover:underline font-medium underline"
              >
                {labels.privacyPolicy}
              </button>
              {labels.consentLabelEnd} <span className="text-red-500">*</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
